import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Money, ShieldCheck, Truck, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// Generate order number
const generateOrderNumber = () => {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.random().toString(36).substring(2, 10).toUpperCase();
  return `NXO-${date}-${random}`;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartSubtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  const shippingCost = cartSubtotal >= 75 ? 0 : 9.99;
  const total = cartSubtotal + shippingCost;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({ ...prev, state: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const requiredFields = ['first_name', 'last_name', 'email', 'phone', 'address', 'city', 'state', 'zip_code'];
    const emptyFields = requiredFields.filter(field => !formData[field]);
    
    if (emptyFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    
    // Show processing message
    toast.loading('Processing your order...', { id: 'order-processing' });

    // Simulate order processing (2-3 seconds)
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    // Create order details
    const orderNumber = generateOrderNumber();
    const estimatedDelivery = new Date();
    estimatedDelivery.setDate(estimatedDelivery.getDate() + 5);
    
    const order = {
      order_number: orderNumber,
      items: cart.items.map(item => ({
        name: item.product.name,
        price: item.product.price,
        quantity: item.quantity,
        image: item.product.images[0]
      })),
      shipping_address: formData,
      payment_method: paymentMethod,
      subtotal: cartSubtotal,
      shipping_cost: shippingCost,
      total: total,
      estimated_delivery: estimatedDelivery.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      created_at: new Date().toISOString()
    };

    // Store order in localStorage for confirmation page
    localStorage.setItem('nexa_last_order', JSON.stringify(order));
    
    // Clear cart
    await clearCart();
    
    // Dismiss loading toast
    toast.dismiss('order-processing');
    
    // Show success
    setOrderDetails(order);
    setOrderSuccess(true);
    setLoading(false);
    
    // Show success toast
    toast.success('Order placed successfully!');
  };

  // If order is successful, show success screen
  if (orderSuccess && orderDetails) {
    return (
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-12" data-testid="order-success-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-8">
            <CheckCircle size={60} weight="fill" className="text-green-600" />
          </div>
          
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-nexa-charcoal mb-4">
            Order Placed Successfully!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Thank you for your order. We have received your order and will process it shortly.
          </p>

          <div className="bg-nexa-stone rounded-lg p-8 mb-8 text-left">
            <div className="flex justify-between items-center mb-6 pb-6 border-b border-nexa-border">
              <div>
                <p className="text-sm text-muted-foreground">Order Number</p>
                <p className="font-serif text-2xl font-semibold text-nexa-charcoal">{orderDetails.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Estimated Delivery</p>
                <p className="font-semibold text-nexa-charcoal">{orderDetails.estimated_delivery}</p>
              </div>
            </div>

            <h3 className="font-semibold text-nexa-charcoal mb-4">Order Summary</h3>
            <div className="space-y-4 mb-6">
              {orderDetails.items.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-medium text-nexa-charcoal">{item.name}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium text-nexa-charcoal">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-nexa-border pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-nexa-charcoal">${orderDetails.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-nexa-charcoal">
                  {orderDetails.shipping_cost === 0 ? 'FREE' : `$${orderDetails.shipping_cost.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-nexa-border">
                <span className="font-semibold text-nexa-charcoal">Total</span>
                <span className="font-serif text-xl font-semibold text-nexa-charcoal">${orderDetails.total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-nexa-border">
              <h4 className="font-semibold text-nexa-charcoal mb-2">Shipping To:</h4>
              <p className="text-muted-foreground">
                {orderDetails.shipping_address.first_name} {orderDetails.shipping_address.last_name}<br />
                {orderDetails.shipping_address.address}<br />
                {orderDetails.shipping_address.city}, {orderDetails.shipping_address.state} {orderDetails.shipping_address.zip_code}
              </p>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold text-nexa-charcoal mb-2">Payment Method:</h4>
              <p className="text-muted-foreground">
                {orderDetails.payment_method === 'cod' ? 'Cash on Delivery' : 'Credit/Debit Card'}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="btn-primary"
              onClick={() => navigate('/shop')}
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
            <Button 
              variant="outline"
              className="border-nexa-charcoal text-nexa-charcoal hover:bg-nexa-charcoal hover:text-white"
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </div>

          <div className="mt-8 p-4 bg-white rounded-lg border border-nexa-border">
            <p className="text-sm text-muted-foreground">
              Questions about your order? Contact us at{' '}
              <a href="mailto:support@nexaorbits.store" className="text-nexa-gold hover:underline">
                support@nexaorbits.store
              </a>{' '}
              or call{' '}
              <a href="tel:+18883511122" className="text-nexa-gold hover:underline">
                +1-888-351-1122
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // If cart is empty, redirect to cart
  if (cart.items.length === 0 && !orderSuccess) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12" data-testid="checkout-page">
      <h1 className="font-serif text-4xl font-medium text-nexa-charcoal mb-8">Checkout</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Customer Information */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Information */}
            <div className="bg-white rounded-lg border border-nexa-border p-6">
              <h2 className="font-serif text-xl font-semibold text-nexa-charcoal mb-6">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="input-last-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-lg border border-nexa-border p-6">
              <h2 className="font-serif text-xl font-semibold text-nexa-charcoal mb-6">Shipping Address</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="input-address"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Select value={formData.state} onValueChange={handleStateChange}>
                      <SelectTrigger className="mt-1" data-testid="select-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="zip_code">ZIP Code *</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      className="mt-1"
                      required
                      data-testid="input-zip"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-lg border border-nexa-border p-6">
              <h2 className="font-serif text-xl font-semibold text-nexa-charcoal mb-6">Payment Method</h2>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="flex items-center space-x-3 p-4 border border-nexa-border rounded-lg cursor-pointer hover:border-nexa-gold transition-colors">
                  <RadioGroupItem value="cod" id="cod" data-testid="payment-cod" />
                  <Label htmlFor="cod" className="flex items-center gap-3 cursor-pointer flex-1">
                    <Money size={24} className="text-nexa-gold" />
                    <div>
                      <p className="font-medium text-nexa-charcoal">Cash on Delivery (COD)</p>
                      <p className="text-sm text-muted-foreground">Pay when your order arrives</p>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 border border-nexa-border rounded-lg cursor-pointer hover:border-nexa-gold transition-colors mt-3 opacity-50">
                  <RadioGroupItem value="card" id="card" disabled data-testid="payment-card" />
                  <Label htmlFor="card" className="flex items-center gap-3 cursor-pointer flex-1">
                    <CreditCard size={24} className="text-nexa-gold" />
                    <div>
                      <p className="font-medium text-nexa-charcoal">Credit/Debit Card</p>
                      <p className="text-sm text-muted-foreground">Coming Soon</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-nexa-border p-6 sticky top-32" data-testid="checkout-summary">
              <h2 className="font-serif text-xl font-semibold text-nexa-charcoal mb-6">Order Summary</h2>

              {/* Items */}
              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-3">
                    <img 
                      src={item.product?.images?.[0]} 
                      alt={item.product?.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-nexa-charcoal line-clamp-1">{item.product?.name}</p>
                      <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                      <p className="text-sm font-medium text-nexa-charcoal">
                        ${((item.product?.price || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-nexa-border pt-4 space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-nexa-charcoal">${cartSubtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-nexa-charcoal">
                    {shippingCost === 0 ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      `$${shippingCost.toFixed(2)}`
                    )}
                  </span>
                </div>
              </div>

              <div className="border-t border-nexa-border pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-semibold text-nexa-charcoal">Total</span>
                  <span className="font-serif text-2xl font-semibold text-nexa-charcoal">${total.toFixed(2)}</span>
                </div>
              </div>

              <Button 
                type="submit"
                className="w-full h-14 btn-gold"
                disabled={loading}
                data-testid="place-order-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner"></span>
                    Processing...
                  </span>
                ) : (
                  'Place Order'
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 mt-6 text-sm text-muted-foreground">
                <ShieldCheck size={18} className="text-nexa-gold" />
                <span>Secure Checkout</span>
              </div>

              <div className="flex items-center gap-2 mt-4 p-3 bg-nexa-stone rounded-lg">
                <Truck size={20} className="text-nexa-gold flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  {shippingCost === 0 
                    ? 'Your order qualifies for FREE shipping!' 
                    : `Add $${(75 - cartSubtotal).toFixed(2)} more for free shipping`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
